import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MarketplaceApiService, Slot } from '../services/marketplace-api.service';
import { MarketplaceAuthService } from '../services/marketplace-auth.service';
import { AuthModalService } from '../services/auth-modal.service';
import { MercadoPagoSdkService } from '../services/mercadopago-sdk.service';
import { EstadoSede, EstadoSedeTono, tonoEstadoSede } from '../models/marketplace.models';
import { SeoService } from '../../shared/seo/seo.service';
import { seoSede } from '../seo-marketplace';

interface Dia { iso: string; dow: string; day: number; mes: string; }

@Component({
  selector: 'app-sede-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  styles: [`
    :host{ display:block; background:var(--bg,#eef2f8); min-height:100vh;
      --ink:#12263a; --muted:#778798; --line:#e9eef5; --card:#fff; }
    *{ box-sizing:border-box; }
    .shell{ max-width:520px; margin:0 auto; padding-bottom:120px; }
    .grid2{ margin:-44px 12px 0; }

    /* ---- Hero ---- */
    .hero{ position:relative; color:#fff; padding:16px 18px 64px;
      border-radius:0 0 26px 26px; overflow:hidden; }
    .hero::after{ content:''; position:absolute; inset:0; background:radial-gradient(120% 90% at 85% 0%, rgba(255,255,255,.18), transparent 60%); pointer-events:none; z-index:1; }
    .hero .portada{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; z-index:0; }
    .hero .portada-ov{ position:absolute; inset:0; z-index:0; }
    .hero .nav{ display:flex; align-items:center; justify-content:space-between; position:relative; z-index:1; }
    .hero .hgroup{ display:flex; gap:13px; align-items:center; position:relative; z-index:1; }
    .hero .logo{ width:58px; height:58px; border-radius:16px; overflow:hidden; flex:none;
      background:rgba(255,255,255,.2); border:2px solid rgba(255,255,255,.45); }
    .hero .logo img{ width:100%; height:100%; object-fit:cover; }
    .circle{ width:40px; height:40px; border-radius:50%; display:grid; place-items:center;
      background:rgba(255,255,255,.22); color:#fff; text-decoration:none; border:0; cursor:pointer;
      font-size:17px; backdrop-filter:blur(4px); }
    .hero .badges{ display:flex; gap:8px; margin:18px 0 10px; position:relative; z-index:1; }
    .chip{ display:inline-flex; align-items:center; gap:5px; background:rgba(255,255,255,.9); color:var(--ink);
      border-radius:999px; padding:5px 11px; font-size:12.5px; font-weight:700; }
    .chip.rubro{ background:rgba(255,255,255,.18); color:#fff; text-transform:capitalize; }
    .hero h1{ margin:0; font-size:1.7rem; line-height:1.12; font-weight:800; position:relative; z-index:1; }
    .hero .sub{ margin-top:8px; font-size:13.5px; opacity:.95; position:relative; z-index:1; display:flex; flex-direction:column; gap:2px; }

    /* ---- Sheet ---- */
    .sheet{ background:var(--card); border-radius:22px; padding:18px 16px 8px; margin-bottom:14px;
      box-shadow:0 12px 34px rgba(15,60,95,.12); position:relative; }
    .h{ font-size:1.02rem; font-weight:800; color:var(--ink); margin:0 0 12px; display:flex;
      align-items:center; justify-content:space-between; gap:10px; }
    .h .meta{ font-size:12.5px; color:var(--muted); font-weight:600; white-space:nowrap; }
    .sec + .sec{ margin-top:22px; }

    /* ---- Calendario (otra fecha) ---- */
    .cal-wrap{ position:relative; display:inline-block; margin-top:10px; }
    .cal{ display:inline-flex; align-items:center; gap:7px; font-size:13px;
      font-weight:700; color:var(--ink); background:#f4f7fb; border:1.5px solid var(--line);
      border-radius:12px; padding:8px 12px; cursor:pointer; }
    .cal:hover{ border-color:var(--accent); }
    .cal .ic{ font-size:15px; }
    /* input nativo detrás del botón: showPicker() lo ancla acá */
    .cal-input{ position:absolute; left:0; bottom:0; width:100%; height:100%; opacity:0; border:0; pointer-events:none; }

    /* ---- Servicios ---- */
    .svc{ display:flex; align-items:center; gap:12px; padding:12px; border:1.5px solid var(--line);
      border-radius:14px; cursor:pointer; transition:border-color .15s, background .15s; margin-bottom:10px; }
    .svc:last-child{ margin-bottom:0; }
    .svc.on{ background:color-mix(in srgb, var(--accent) 7%, #fff); }
    .svc .thumb{ width:56px; height:56px; border-radius:12px; overflow:hidden; flex:none; background:var(--line); }
    .svc .thumb img{ width:100%; height:100%; object-fit:cover; display:block; }
    .svc .info{ flex:1; min-width:0; }
    .svc h3{ margin:0 0 2px; font-size:.98rem; color:var(--ink); }
    .svc .d{ font-size:12.5px; color:var(--muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .svc .dur{ font-size:12px; color:#9aa7b6; margin-top:3px; }
    .svc .price{ text-align:right; white-space:nowrap; }
    .svc .price .b{ font-size:1.05rem; font-weight:800; color:var(--ink); }
    .svc .price .adel{ font-size:11.5px; color:var(--accent); font-weight:700; }
    .tick{ width:22px; height:22px; border-radius:50%; border:2px solid var(--line); display:grid; place-items:center; flex:none; color:#fff; font-size:12px; }
    .svc.on .tick{ border-color:var(--accent); background:var(--accent); }

    /* ---- Días ---- */
    .days{ display:flex; gap:8px; overflow-x:auto; padding:2px 2px 6px; scrollbar-width:none; }
    .days::-webkit-scrollbar{ display:none; }
    .day{ flex:0 0 auto; width:52px; padding:9px 0; border-radius:14px; border:1.5px solid var(--line);
      background:#fff; text-align:center; cursor:pointer; transition:.15s; }
    .day .dw{ font-size:11px; color:var(--muted); font-weight:600; }
    .day .dn{ font-size:1.05rem; font-weight:800; color:var(--ink); margin-top:2px; }
    .day.on{ border-color:transparent; }
    .day.on .dw, .day.on .dn{ color:#fff; }

    /* ---- Slots ---- */
    .slots{ display:grid; grid-template-columns:repeat(3,1fr); gap:9px; }
    .slot{ padding:11px 6px; border-radius:12px; border:1.5px solid var(--line); background:#fff;
      color:var(--ink); font-weight:700; font-size:13.5px; cursor:pointer; transition:.15s; text-align:center; }
    .slot.on{ color:#fff; border-color:transparent; }
    .state{ color:var(--muted); font-size:13px; padding:8px 2px; }

    /* ---- Reserva ok / pago ---- */
    .ok{ background:#eefaf1; border:1px solid #c6ebd3; color:#177a45; border-radius:14px; padding:14px; margin-top:14px; font-size:13.5px; }
    .ok b{ color:#0f5c34; }
    .pay{ margin-top:10px; width:100%; border:0; border-radius:12px; padding:12px; color:#fff; font-weight:800; cursor:pointer; }
    .paid{ margin-top:8px; color:#177a45; font-weight:800; font-size:14px; }
    #mp-brick{ margin-top:12px; }
    .muted{ color:var(--muted); font-size:12.5px; margin-top:6px; }

    /* ---- CTA fijo ---- */
    .cta{ position:fixed; left:0; right:0; bottom:0; z-index:20; background:rgba(255,255,255,.94);
      backdrop-filter:blur(8px); border-top:1px solid var(--line);
      padding:12px 16px calc(12px + env(safe-area-inset-bottom));
      display:flex; align-items:center; gap:14px; }
    .cta .lbl{ flex:1; min-width:0; }
    .cta .lbl .t{ font-size:12px; color:var(--muted); font-weight:600; }
    .cta .lbl .v{ font-size:1.12rem; font-weight:800; color:var(--ink); }
    .cta .go{ border:0; border-radius:14px; padding:14px 24px; color:#fff; font-weight:800; font-size:.98rem; cursor:pointer; white-space:nowrap; }
    .cta .go:disabled{ opacity:.45; cursor:not-allowed; }
    @media (min-width:560px){ .cta{ left:50%; transform:translateX(-50%); max-width:520px; border-radius:18px 18px 0 0; } }

    /* ---- Tablet / Desktop: 2 columnas ---- */
    @media (min-width:900px){
      .shell{ max-width:980px; }
      .hero{ border-radius:0 0 30px 30px; padding:20px 34px 70px; }
      .hero h1{ font-size:2rem; }
      .grid2{ display:grid; grid-template-columns:1.04fr 1fr; gap:18px; align-items:start; margin:-48px 16px 0; }
      .sheet{ margin-bottom:0; padding:22px 20px 12px; }
      .sheet.span{ grid-column:1 / -1; }
      .slots{ grid-template-columns:repeat(4,1fr); }
      .cta{ max-width:980px; border-radius:18px 18px 0 0; }
      .cta .go{ padding:14px 34px; }
    }
    @media (min-width:1200px){
      .shell{ max-width:1100px; }
      .grid2{ grid-template-columns:1fr 1fr; }
    }

    .loading{ text-align:center; color:var(--muted); padding:80px 20px; }

    /* ---- Redes ---- */
    .redes{ display:flex; flex-wrap:wrap; gap:9px; }
    .red{ display:inline-flex; align-items:center; gap:7px; text-decoration:none; border:1.5px solid var(--line);
      border-radius:999px; padding:8px 14px; font-size:13px; font-weight:700; color:var(--ink); transition:.15s; background:#fff; }
    .red:hover{ border-color:var(--accent); color:var(--accent); }
    .red .ic{ font-size:15px; line-height:1; }

    /* ---- Ubicación / mapa ---- */
    .map{ width:100%; height:200px; border-radius:14px; overflow:hidden; border:1px solid var(--line); }
    .map iframe{ width:100%; height:100%; border:0; display:block; }
    .maps-link{ display:inline-flex; align-items:center; gap:6px; margin-top:10px; text-decoration:none;
      color:var(--accent); font-weight:700; font-size:13.5px; }
    .maps-link:hover{ text-decoration:underline; }
    .addr{ font-size:13px; color:var(--muted); margin-bottom:10px; }

    /* ---- Chip de estado operativo (texto armado por el backend) ---- */
    .est{ align-self:flex-start; display:inline-flex; align-items:center; gap:5px; border-radius:999px;
      padding:3px 10px; font-size:11.5px; font-weight:700; line-height:1.5; border:1px solid transparent; margin-top:3px; }
    .est::before{ content:''; width:7px; height:7px; border-radius:50%; background:currentColor; flex:none; }
    .est-ok{ background:#eefaf1; color:#177a45; border-color:#c6ebd3; }
    .est-busy{ background:#fff6e6; color:#b45309; border-color:#f3ddb2; }
    .est-off{ background:#f1f4f8; color:#7c8794; border-color:#e2e8f0; }

    /* ---- Aviso: la sede no atiende hoy (no bloquea elegir otra fecha) ---- */
    .aviso{ display:flex; gap:9px; align-items:flex-start; background:#f1f4f8; border:1px solid #e2e8f0;
      border-left:3px solid #7c8794; border-radius:12px; padding:11px 12px; margin-bottom:12px; }
    .aviso .ic{ font-size:15px; line-height:1.35; flex:none; }
    .aviso .t{ font-size:13px; color:var(--ink); font-weight:800; }
    .aviso .d{ font-size:12.5px; color:var(--muted); margin-top:2px; }

    /* ---- Hint CTA ---- */
    .cta .lbl .hint2{ font-size:12px; color:#b45309; font-weight:600; }
  `],
  template: `
  @if (marca && sede) {
    <div class="shell">
      <div class="hero" [class.has-portada]="marca.portadaUrl"
           [style.--accent]="color"
           [style.background]="'linear-gradient(150deg,'+color+' 0%, '+shade+' 100%)'">
        @if (marca.portadaUrl) {
          <img class="portada" [src]="marca.portadaUrl" alt="" loading="lazy" />
          <div class="portada-ov" [style.background]="'linear-gradient(150deg,'+color+'cc 0%, '+shade+'e6 100%)'"></div>
        }
        <div class="nav">
          <a class="circle" [routerLink]="['/marketplace', marca.slug]" aria-label="Volver">←</a>
        </div>
        <div class="badges">
          @if (marca.rating) { <span class="chip">★ {{ marca.rating }}</span> }
          @if (marca.rubro) { <span class="chip rubro">{{ marca.rubro }}</span> }
        </div>
        <div class="hgroup">
          @if (marca.logoUrl) { <div class="logo"><img [src]="marca.logoUrl" alt="" loading="lazy" /></div> }
          <div>
            <h1>{{ marca.nombre }}</h1>
            <div class="sub">
              <span>{{ sede.nombre }}</span>
              <span>📍 {{ sede.direccion }}</span>
              @if (sede.horario) { <span>🕒 {{ sede.horario }}</span> }
              @if (tonoEstado; as t) {
                <span class="est" [ngClass]="'est-' + t" [attr.title]="sede.estado?.motivo">{{ sede.estado.etiqueta }}</span>
              }
            </div>
          </div>
        </div>
      </div>

      <div class="grid2" [style.--accent]="color">
        <!-- Col 1: Servicios + redes + ubicación -->
        <div class="sheet">
          <div class="sec">
            <div class="h">Servicios en esta sede <span class="meta">{{ servicios.length }}</span></div>
            @for (sv of servicios; track sv.id) {
              <div class="svc" [class.on]="sel?.id === sv.id" (click)="selectService(sv)">
                @if (sv.fotoUrl) { <div class="thumb"><img [src]="sv.fotoUrl" alt="" loading="lazy" /></div> }
                @else { <div class="tick">@if (sel?.id === sv.id) { ✓ }</div> }
                <div class="info">
                  <h3>{{ sv.nombre }}</h3>
                  @if (sv.descripcion) { <div class="d">{{ sv.descripcion }}</div> }
                  <div class="dur">⏱ {{ sv.duracionMin }} min</div>
                </div>
                <div class="price">
                  <div class="b">S/ {{ sv.precioBruto }}</div>
                  <div class="adel">reserva S/ {{ adelanto(sv) }}</div>
                </div>
              </div>
            }
          </div>

          <!-- Redes de la marca -->
          @if (hasRedes) {
            <div class="sec">
              <div class="h">Redes</div>
              <div class="redes">
                @if (marca.redes?.instagram) {
                  <a class="red" [href]="marca.redes.instagram" target="_blank" rel="noopener"><span class="ic">📷</span> Instagram</a>
                }
                @if (marca.redes?.facebook) {
                  <a class="red" [href]="marca.redes.facebook" target="_blank" rel="noopener"><span class="ic">👍</span> Facebook</a>
                }
                @if (marca.redes?.tiktok) {
                  <a class="red" [href]="marca.redes.tiktok" target="_blank" rel="noopener"><span class="ic">🎵</span> TikTok</a>
                }
                @if (marca.redes?.web) {
                  <a class="red" [href]="marca.redes.web" target="_blank" rel="noopener"><span class="ic">🌐</span> Web</a>
                }
              </div>
            </div>
          }

          <!-- Ubicación -->
          <div class="sec">
            <div class="h">Ubicación</div>
            @if (sede.direccion) { <div class="addr">📍 {{ sede.direccion }}</div> }
            @if (mapEmbed) {
              <div class="map">
                <iframe [src]="mapEmbed" loading="lazy" referrerpolicy="no-referrer-when-downgrade" title="Mapa de la sede"></iframe>
              </div>
            }
            <a class="maps-link" [href]="mapsHref" target="_blank" rel="noopener">Ver en Google Maps →</a>
          </div>
        </div>

        <!-- Col 2: Fecha + Hora -->
        @if (sel && !reserva) {
          <div class="sheet">
            <!-- Fecha -->
            <div class="sec">
              <div class="h">Elegí el día <span class="meta">{{ mesActual }}</span></div>
              @if (cerradaHoy) {
                <div class="aviso">
                  <span class="ic">🔒</span>
                  <div>
                    <div class="t">Hoy esta sede ya no atiende</div>
                    <div class="d">{{ sede.estado.etiqueta }}</div>
                    @if (sede.estado.motivo) { <div class="d">{{ sede.estado.motivo }}</div> }
                    <div class="d">Elegí otra fecha para ver los horarios disponibles.</div>
                  </div>
                </div>
              }
              <div class="days">
                @for (d of dias; track d.iso) {
                  <div class="day" [class.on]="d.iso === fecha"
                       [style.background]="d.iso === fecha ? color : ''"
                       (click)="selectDia(d)">
                    <div class="dw">{{ d.dow }}</div>
                    <div class="dn">{{ d.day }}</div>
                  </div>
                }
              </div>
              <div class="cal-wrap">
                <button type="button" class="cal" (click)="abrirCalendario(fechaInput)">
                  <span class="ic">📅</span> Otra fecha
                </button>
                <input #fechaInput type="date" class="cal-input" [min]="hoyIso" [value]="fecha"
                       (change)="onPickFecha($event)" tabindex="-1" aria-label="Elegir otra fecha" />
              </div>
            </div>

            <!-- Horario -->
            <div class="sec">
              <div class="h">Elegí la hora
                @if (!cargandoSlots && slots.length) { <span class="meta">{{ slots.length }} disponibles</span> }
              </div>
              @if (cargandoSlots) { <div class="state">Buscando horarios…</div> }
              @else if (slots.length) {
                <div class="slots">
                  @for (s of slots; track s.inicio) {
                    <button class="slot" type="button" [class.on]="slotSel?.inicio === s.inicio"
                            [style.background]="slotSel?.inicio === s.inicio ? color : ''"
                            (click)="slotSel = s">{{ s.inicio }}</button>
                  }
                </div>
              } @else {
                <div class="state">No hay horarios para esta fecha. Probá con otro día.</div>
              }
            </div>
          </div>
        }

        <!-- Reserva creada + pago (ancho completo). OCULTO en el MVP WhatsApp. -->
        @if (!mvpWhatsapp && reserva) {
          <div class="sheet span">
            <div class="ok">
              ✓ Reserva <b>{{ reserva.codigo }}</b> · {{ sel?.nombre }} · {{ diaLabel }} {{ horaReserva }}<br>
              Adelanto <b>S/ {{ reserva.adelantoRequerido }}</b> de S/ {{ reserva.precioBruto }}.
              @if (!pagoConfirmado) {
                @if (!mostrarBrick) {
                  <button class="pay" [style.background]="color" (click)="pagar()">Pagar adelanto</button>
                }
                <div id="mp-brick" [style.display]="mostrarBrick ? 'block' : 'none'"></div>
              } @else {
                <div class="paid">✓ Pago {{ pagoEstadoTxt }}. {{ reservaEstadoTxt }}</div>
              }
              @if (pagoMsg) { <div class="muted">{{ pagoMsg }}</div> }
            </div>
          </div>
        }
      </div>
    </div>

    <!-- CTA fijo -->
    @if (sel && !reserva) {
      <div class="cta" [style.--accent]="color">
        <div class="lbl">
          @if (mvpWhatsapp && !sede.whatsapp) {
            <div class="t">Reservar</div>
            <div class="hint2">Esta sede aún no tiene WhatsApp configurado</div>
          } @else {
            <div class="t">{{ sel.nombre }} · adelanto ref.</div>
            <div class="v">S/ {{ adelanto(sel) }}</div>
          }
        </div>
        @if (mvpWhatsapp) {
          <button class="go" type="button" [style.background]="color" [disabled]="!slotSel || !sede.whatsapp"
                  (click)="reservarWhatsapp()">
            {{ !sede.whatsapp ? 'No disponible' : (slotSel ? 'Reservar por WhatsApp' : 'Elegí una hora') }}
          </button>
        } @else {
          <button class="go" type="button" [style.background]="color" [disabled]="!slotSel"
                  (click)="confirmar(sel, slotSel!)">
            {{ slotSel ? 'Reservar ' + slotSel.inicio : 'Elegí una hora' }}
          </button>
        }
      </div>
    }
  } @else {
    <div class="loading">{{ error ? 'No se encontró la sede.' : 'Cargando…' }}</div>
  }
  `,
})
export class SedeDetailComponent implements OnInit {
  marca?: any;
  sede?: any;
  error = false;
  color = '#00A7DB';
  shade = '#0f6ea8';

  /** Servicios ofrecidos EN esta sede (fallback al catálogo de la marca si el backend no los trae). */
  servicios: any[] = [];

  /** MVP: reservar por WhatsApp; el flujo de reserva+pago MP queda oculto detrás de esta bandera. */
  mvpWhatsapp = true;
  /** URL saneada del iframe de Google Maps embed (null si la sede no tiene lat/lng). */
  mapEmbed: SafeResourceUrl | null = null;

  sel?: any;
  fecha = '';
  hoyIso = '';
  dias: Dia[] = [];
  mesActual = '';
  slots: Slot[] = [];
  slotSel?: Slot;
  cargandoSlots = false;
  reserva?: any;
  horaReserva = '';
  diaLabel = '';
  pagoMsg = '';

  // pago con Checkout Bricks
  mostrarBrick = false;
  pagoConfirmado = false;
  pagoEstadoTxt = '';
  reservaEstadoTxt = '';
  private brickController?: any;

  private static readonly DOW = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  private static readonly MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

  constructor(
    private route: ActivatedRoute,
    private api: MarketplaceApiService,
    private auth: MarketplaceAuthService,
    private authModal: AuthModalService,
    private sdk: MercadoPagoSdkService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private seo: SeoService,
  ) {}

  ngOnInit(): void {
    this.buildDias();
    this.route.paramMap.subscribe((p) => {
      const marcaSlug = p.get('marca') ?? '';
      const sedeSlug = p.get('sede') ?? '';
      this.api.getMarca(marcaSlug).subscribe({
        next: (m) => {
          this.marca = m;
          this.sede = m?.sedes?.find((s: any) => s.slug === sedeSlug);
          if (m) {
            this.color = MarketplaceApiService.colorDe(m);
            this.shade = this.darken(this.color);
          }
          if (!m || !this.sede) {
            this.error = true;
            this.seo.set({ titulo: 'Negocio no encontrado', descripcion: 'Este negocio no existe o ya no está publicado en Aura.', path: '/marketplace', noindex: true }); // ver marca-detail
          } else this.seo.set(seoSede(m, this.sede));
          this.buildMapEmbed();
          // Servicios de LA SEDE (no del catálogo completo); fallback a los de la marca.
          this.servicios = this.sede?.servicios ?? this.marca?.servicios ?? [];
          // Auto-selecciona el primer servicio para mostrar fecha/hora de una.
          if (this.servicios.length) this.selectService(this.servicios[0]);
          this.cdr.detectChanges();
        },
        error: () => {
          this.error = true;
          this.cdr.detectChanges();
        },
      });
    });
  }

  private static iso(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  /** 14 días desde `fromIso` (o desde hoy). `hoyIso` es siempre hoy → nunca se ofrece el pasado. */
  private buildDias(fromIso?: string): void {
    const hoy = new Date();
    this.hoyIso = SedeDetailComponent.iso(hoy);
    const base = fromIso ? new Date(fromIso + 'T00:00:00') : hoy;
    const arr: Dia[] = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + i);
      arr.push({
        iso: SedeDetailComponent.iso(d),
        dow: SedeDetailComponent.DOW[d.getDay()],
        day: d.getDate(),
        mes: SedeDetailComponent.MES[d.getMonth()],
      });
    }
    this.dias = arr;
    const first = arr[0];
    this.mesActual = `${first.mes} ${new Date(first.iso + 'T00:00:00').getFullYear()}`;
    this.mesActual = this.mesActual.charAt(0).toUpperCase() + this.mesActual.slice(1);
  }

  /** Abre el date picker nativo. Chrome no lo abre al clickear el cuerpo del input → showPicker(). */
  abrirCalendario(inp: HTMLInputElement): void {
    const anyInp = inp as any;
    if (typeof anyInp.showPicker === 'function') {
      try { anyInp.showPicker(); return; } catch { /* fallback abajo */ }
    }
    inp.focus();
    inp.click();
  }

  /** Calendario libre: cualquier fecha >= hoy. Si cae fuera del strip visible, lo reubica. */
  onPickFecha(e: Event): void {
    const v = (e.target as HTMLInputElement).value;
    if (!v || v < this.hoyIso) return; // el min del input ya bloquea el pasado; doble guarda
    this.fecha = v;
    this.slotSel = undefined;
    const last = this.dias[this.dias.length - 1]?.iso ?? '';
    if (v < this.dias[0]?.iso || v > last) this.buildDias(v); // reubica el strip si quedó fuera
    this.cargarSlots();
  }

  adelanto(sv: any): number {
    return Math.round((sv.precioBruto * sv.adelantoPct) / 100);
  }

  /** Estado operativo que el backend embebe en la sede (puede no venir todavía). */
  get estado(): EstadoSede | undefined {
    return this.sede?.estado as EstadoSede | undefined;
  }

  /**
   * Tono del chip de estado. `null` si el backend no mandó `estado` (o llegó sin
   * etiqueta): en ese caso no se pinta chip — nunca se asume "Abierto" por defecto.
   */
  get tonoEstado(): EstadoSedeTono | null {
    return tonoEstadoSede(this.estado);
  }

  /**
   * El servidor dice que la sede ya no vuelve a abrir hoy Y el día elegido es hoy.
   *
   * Se lee `estado.cerradaHoy` tal cual en vez de inferirlo de `estado === 'CERRADA'`:
   * ese código es "cerrada en este instante" y daba falso positivo con una sede que
   * abre más tarde hoy, o que ya atendió y cerró. Comparación estricta contra `true`
   * para que un backend sin el campo (`undefined`) no dispare el aviso.
   *
   * Avisa, pero NO bloquea nada: el strip, el calendario libre y los slots siguen
   * operativos para cualquier otra fecha.
   */
  get cerradaHoy(): boolean {
    return this.estado?.cerradaHoy === true && !!this.fecha && this.fecha === this.hoyIso;
  }

  /** ¿La marca tiene al menos una red social configurada? */
  get hasRedes(): boolean {
    const r = this.marca?.redes;
    return !!(r && (r.instagram || r.facebook || r.tiktok || r.web));
  }

  /** Link a Google Maps: usa mapsUrl si existe; si no, lat/lng; si no, la dirección. */
  get mapsHref(): string {
    const s = this.sede;
    if (s?.mapsUrl) return s.mapsUrl;
    if (s?.lat != null && s?.lng != null) return `https://www.google.com/maps?q=${s.lat},${s.lng}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s?.direccion ?? s?.nombre ?? '')}`;
  }

  /** Construye la URL saneada del iframe embed (solo si hay coordenadas; sin API key). */
  private buildMapEmbed(): void {
    const s = this.sede;
    if (s?.lat != null && s?.lng != null) {
      const url = `https://maps.google.com/maps?q=${s.lat},${s.lng}&z=16&output=embed`;
      this.mapEmbed = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    } else {
      this.mapEmbed = null;
    }
  }

  /** Etiqueta legible del día actualmente elegido (para el mensaje de WhatsApp). */
  private get diaLabelSel(): string {
    const d = this.dias.find((x) => x.iso === this.fecha);
    return d ? `${d.dow} ${d.day} de ${d.mes}` : this.fecha;
  }

  /** MVP: arma el mensaje y abre WhatsApp en una pestaña nueva. No requiere login. */
  reservarWhatsapp(): void {
    if (!this.sede?.whatsapp || !this.sel || !this.slotSel) return;
    const tel = String(this.sede.whatsapp).replace(/[^\d]/g, '');
    const msg =
      `Hola! Quiero reservar ${this.sel.nombre} en ${this.marca.nombre} - ${this.sede.nombre} ` +
      `para el ${this.diaLabelSel} a las ${this.slotSel.inicio}. ` +
      `(Adelanto ref: S/ ${this.adelanto(this.sel)}).`;
    const url = `https://wa.me/${tel}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  }

  selectService(sv: any): void {
    this.sel = sv;
    this.reserva = undefined;
    this.slotSel = undefined;
    this.pagoMsg = '';
    if (!this.fecha) this.fecha = this.dias[1]?.iso ?? this.dias[0]?.iso ?? '';
    this.cargarSlots();
  }

  selectDia(d: Dia): void {
    this.fecha = d.iso;
    this.slotSel = undefined;
    this.cargarSlots();
  }

  /** Profesional de ESTA sede (multi-sede); si la sede no tiene, cae al de la marca. */
  get profesionalId(): number | null {
    return this.sede?.profesionalId ?? this.marca?.profesionalId ?? null;
  }

  cargarSlots(): void {
    this.reserva = undefined;
    if (!this.profesionalId || !this.sel) {
      this.slots = [];
      return;
    }
    this.cargandoSlots = true;
    this.api.getAvailability(this.profesionalId, this.fecha, this.sel.duracionMin, this.sede?.id).subscribe({
      next: (s) => {
        this.slots = s;
        this.cargandoSlots = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.slots = [];
        this.cargandoSlots = false;
        this.cdr.detectChanges();
      },
    });
  }

  confirmar(sv: any, slot: Slot): void {
    if (!this.auth.isLogged()) {
      // requiere sesión → abre el modal contextual y continúa la reserva sin perder el slot
      this.authModal.require('login').then((ok) => {
        if (ok) this.crearReserva(sv, slot);
      });
      return;
    }
    this.crearReserva(sv, slot);
  }

  private crearReserva(sv: any, slot: Slot): void {
    this.api
      .createReserva({
        idServicioPrecio: sv.id,
        idProfesional: this.profesionalId ?? undefined,
        idSede: this.sede.id,
        fechaHoraInicio: `${this.fecha}T${slot.inicio}:00`,
      })
      .subscribe({
        next: (r) => {
          this.reserva = r;
          this.horaReserva = slot.inicio;
          const d = this.dias.find((x) => x.iso === this.fecha);
          this.diaLabel = d ? `${d.dow} ${d.day}` : this.fecha;
          this.cdr.detectChanges();
        },
        error: (e) => {
          this.pagoMsg = 'No se pudo crear la reserva: ' + (e?.error?.message ?? 'error');
          this.cdr.detectChanges();
        },
      });
  }

  pagar(): void {
    if (!this.auth.isLogged()) {
      // la sesión pudo expirar entre crear la reserva y pagar → reautentica y sigue el pago
      this.authModal.require('login').then((ok) => {
        if (ok) this.montarBrick();
      });
      return;
    }
    this.montarBrick();
  }

  /** Monta el Payment Brick de Mercado Pago (formulario de tarjeta embebido). */
  private async montarBrick(): Promise<void> {
    this.pagoMsg = '';
    this.mostrarBrick = true;
    this.cdr.detectChanges(); // asegura que el contenedor #mp-brick exista en el DOM
    try {
      const mp = await this.sdk.load();
      if (this.brickController) {
        try { await this.brickController.unmount(); } catch { /* noop */ }
      }
      this.brickController = await mp.bricks().create('payment', 'mp-brick', {
        initialization: {
          amount: Number(this.reserva.adelantoRequerido),
          payer: { email: this.auth.user()?.email ?? '' },
        },
        customization: {
          paymentMethods: { creditCard: 'all', debitCard: 'all' },
        },
        callbacks: {
          onReady: () => {},
          onSubmit: ({ formData }: any) =>
            new Promise<void>((resolve, reject) => {
              this.api.payBrick(this.reserva.id, 'ADELANTO', formData).subscribe({
                next: (r) => {
                  this.onPagoResult(r);
                  resolve();
                },
                error: (e) => {
                  this.pagoMsg = 'El pago no se pudo procesar: ' + (e?.error?.message ?? 'error');
                  this.cdr.detectChanges();
                  reject();
                },
              });
            }),
          onError: (err: any) => {
            this.pagoMsg = 'Error en el formulario de pago.';
            console.error('Brick error', err);
            this.cdr.detectChanges();
          },
        },
      });
    } catch (e: any) {
      this.mostrarBrick = false;
      this.pagoMsg = e?.message ?? 'No se pudo iniciar el pago.';
      this.cdr.detectChanges();
    }
  }

  private onPagoResult(r: any): void {
    const status = r?.status;
    if (status === 'approved') {
      this.pagoConfirmado = true;
      this.pagoEstadoTxt = 'aprobado';
      this.reservaEstadoTxt = r?.confirmada
        ? 'Tu cita quedó CONFIRMADA.'
        : 'Reserva registrada; abona el 50% para confirmar.';
      this.mostrarBrick = false;
      if (this.brickController) {
        try { this.brickController.unmount(); } catch { /* noop */ }
      }
    } else if (status === 'rejected') {
      this.pagoMsg = 'Pago rechazado' + (r?.detail ? ` (${r.detail})` : '') + '. Probá con otra tarjeta.';
    } else {
      this.pagoMsg = 'Pago en revisión. Te confirmaremos apenas se acredite.';
    }
    this.cdr.detectChanges();
  }

  /** Oscurece un hex para el degradado del hero. */
  private darken(hex: string): string {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return hex;
    const f = (h: string) => Math.max(0, Math.round(parseInt(h, 16) * 0.62));
    return `rgb(${f(m[1])},${f(m[2])},${f(m[3])})`;
  }
}
